import { useEffect, useState } from 'react'
import { MultiSelect } from 'react-multi-select-component'
import PrefillEditFieldsEditor from './PrefillEditFieldsEditor'
import { PrefillEditFieldDefinition } from './prefillFieldSettingsUtils'
import { getConditionalRenderFields, getOperatorOptions } from './conditionalRenderUtils'

interface IProps {
  setFormElements: any
  selectedElement: any
  selectedField: number
  setTogglerFieldIndex: (fieldId: number) => void
  currentPageIndex: number
}

const FieldConditionalCustomization: React.FC<IProps> = ({
  setFormElements,
  selectedElement,
  selectedField,
  setTogglerFieldIndex,
  currentPageIndex
}) => {
  const condition = selectedElement?.includeOnCondition || {}
  const triggerField = selectedElement

  const [checkboxSelections, setCheckboxSelections] = useState([{ label: '', value: '' }])

  useEffect(() => {
    if (!selectedElement?.includeOnCondition?.conditionActive) return
    if (condition.basedOnElementOfIndex === selectedElement.fieldId) return

    setTogglerFieldIndex(selectedElement.fieldId)
    setFormElements((prev: any) => {
      const updated = [...prev]
      const currentPage = [...updated[currentPageIndex]]
      const field = currentPage[selectedField]
      if (!field) return prev

      currentPage[selectedField] = {
        ...field,
        includeOnCondition: {
          ...(field.includeOnCondition || {}),
          conditionActive: true,
          basedOnElementOfIndex: selectedElement.fieldId,
          comparisionOperation:
            field.includeOnCondition?.comparisionOperation ||
            getOperatorOptions(selectedElement.type)[0] ||
            '='
        }
      }
      updated[currentPageIndex] = currentPage
      return updated
    })
  }, [
    selectedElement.fieldId,
    selectedElement.type,
    selectedElement?.includeOnCondition?.conditionActive,
    condition.basedOnElementOfIndex
  ])

  useEffect(() => {
    const stored = condition.forValue
    if (
      triggerField?.type === 'checkbox' &&
      stored &&
      typeof stored === 'object' &&
      Array.isArray(stored.selectedx)
    ) {
      setCheckboxSelections(stored.selectedx)
    }
  }, [triggerField.fieldId, triggerField?.type, condition.forValue])

  useEffect(() => {
    if (triggerField?.type !== 'checkbox') return

    setFormElements((prev: any) => {
      const updated = [...prev]
      const currentPage = [...updated[currentPageIndex]]
      const field = currentPage[selectedField]
      if (!field) return prev

      let str = ''
      checkboxSelections.forEach((entry: any) => {
        str += `${entry.value};`
      })
      const arrayVals = checkboxSelections.map((entry: any) => entry.value)

      currentPage[selectedField] = {
        ...field,
        includeOnCondition: {
          ...field.includeOnCondition,
          forValue: { selectedx: checkboxSelections, arrayVals, str }
        }
      }
      updated[currentPageIndex] = currentPage
      return updated
    })
  }, [checkboxSelections, triggerField?.type])

  const updateIncludeOnCondition = (updates: Record<string, unknown>) => {
    setFormElements((prev: any) => {
      const updated = [...prev]
      const currentPage = [...updated[currentPageIndex]]
      const field = currentPage[selectedField]
      if (!field) return prev

      currentPage[selectedField] = {
        ...field,
        includeOnCondition: {
          ...(field.includeOnCondition || {}),
          basedOnElementOfIndex: selectedElement.fieldId,
          ...updates
        }
      }
      updated[currentPageIndex] = currentPage
      return updated
    })
  }

  const updateConditionalRenderFields = (nextFields: PrefillEditFieldDefinition[]) => {
    setFormElements((prev: any) => {
      const updated = [...prev]
      const currentPage = [...updated[currentPageIndex]]
      const field = currentPage[selectedField]
      if (!field) return prev

      currentPage[selectedField] = {
        ...field,
        conditionalRenderFields: nextFields
      }
      updated[currentPageIndex] = currentPage
      return updated
    })
  }

  const scalarValue = typeof condition.forValue === 'object' ? '' : String(condition.forValue || '')

  const renderValueInput = () => {
    switch (triggerField.type) {
      case 'radio':
      case 'select':
      case 'switch':
        return (
          <select
            className='form-select form-select-sm'
            value={scalarValue}
            onChange={(e) => updateIncludeOnCondition({ forValue: e.target.value })}
          >
            <option value=''>Select value...</option>
            {triggerField.type === 'switch' ? (
              <>
                <option value='true'>true</option>
                <option value='false'>false</option>
              </>
            ) : (
              triggerField.options?.map((option: any, index: number) => (
                <option key={option.id || index} value={option.text}>
                  {option.text}
                </option>
              ))
            )}
          </select>
        )
      case 'checkbox':
        return (
          <MultiSelect
            options={(triggerField.options || []).map((option: any) => ({
              label: option.text,
              value: option.text
            }))}
            value={checkboxSelections}
            onChange={setCheckboxSelections}
            labelledBy='Select checkbox values'
          />
        )
      default:
        return (
          <input
            type='text'
            className='form-control form-control-sm'
            placeholder='Value that shows the fields below'
            value={scalarValue}
            onChange={(e) => updateIncludeOnCondition({ forValue: e.target.value })}
          />
        )
    }
  }

  return (
    <div className='conditional-render-customization'>
      <div className='p-3 mb-3 '>
        <h6 className='fw-bold mb-3'>When this field matches</h6>
        <div className='row g-2 align-items-end'>
          <div className='col-md-5'>
            <label className='form-label'>Field</label>
            <div className='form-control form-control-sm bg-light fw-semibold'>
              {triggerField.label || triggerField.type}
            </div>
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Operator</label>
            <select
              className='form-select form-select-sm'
              value={condition.comparisionOperation || '='}
              onChange={(e) => updateIncludeOnCondition({ comparisionOperation: e.target.value })}
            >
              {getOperatorOptions(triggerField.type).map((operator) => (
                <option key={operator} value={operator}>
                  {operator}
                </option>
              ))}
            </select>
          </div>
          <div className='col-md-4'>
            <label className='form-label'>Value</label>
            {renderValueInput()}
          </div>
        </div>
        <p className='text-dark small mb-0 mt-2'>
          This field stays visible on the form. The fields you add below appear only when this
          condition matches.
        </p>
      </div>

      <div className='border rounded p-3 bg-white'>
        <h6 className='fw-bold mb-2'>Fields to show</h6>
        <p className='text-muted small mb-3'>
          Add the extra fields that should appear when the condition above is true.
        </p>
        <PrefillEditFieldsEditor
          variant='conditional-render'
          parentFieldId={selectedElement.fieldId}
          editFields={getConditionalRenderFields(selectedElement)}
          onChange={updateConditionalRenderFields}
        />
      </div>
    </div>
  )
}

export default FieldConditionalCustomization
